/**
 * Document generation screen — template selector, in-context document preview
 * with inline form fields, and PDF generation. Designed to be mounted inside
 * a dialog or at its own route in the future.
 */
import { useMemo, useState } from 'react'

import type { PatientWithOwners } from '@snootsvet/shared'
import { formatWeight, SNOOTS_HQ } from '@snootsvet/shared'

import { Label } from '@/frontend/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import {
  type DocumentId,
  documentList,
  getDocumentTemplate,
} from '@/frontend/features/printing/documents/get-document-template'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useListPatientVitals } from '@/frontend/hooks/api/patients/use-list-patient-vitals'

// Global react-pdf config (e.g. hyphenation).
import '@/frontend/features/printing/pdf-setup'

interface GenerateDocumentScreenProps {
  patient: PatientWithOwners
}

/** Main document generation UI — template picker, document preview, and generate button. */
export function GenerateDocumentScreen({ patient }: GenerateDocumentScreenProps) {
  const { data: vitals } = useListPatientVitals(patient.id)
  const [templateId, setTemplateId] = useState<DocumentId | null>(null)
  const { region } = useViewerRegion()

  const doc = templateId ? getDocumentTemplate(templateId) : undefined

  const latestWeight = vitals?.[0]?.weight
    ? formatWeight(Number.parseFloat(vitals[0].weight), region)
    : null

  // Documents may pin their own clinic (e.g. procedures carried out at Bounds
  // Green); everything else is issued from HQ.
  const context: DocumentContext = useMemo(
    () => ({
      patient,
      owner: patient.owner,
      clinic: doc?.clinic ?? SNOOTS_HQ,
      weight: latestWeight,
    }),
    [patient, latestWeight, doc]
  )

  const selectItems = documentList.map((d) => ({ value: d.id, label: d.label }))

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Document template</Label>
        <Select
          value={templateId}
          onValueChange={(v) => setTemplateId(v as DocumentId)}
          items={selectItems}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a template…" />
          </SelectTrigger>
          <SelectContent>
            {documentList.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {doc && <doc.Editor key={doc.id} context={context} patientName={patient.name} />}
    </div>
  )
}
